import { createFileRoute, Link, notFound, type ErrorComponentProps } from '@tanstack/react-router'
import { z } from 'zod'
import { AccommodationDetailContent } from '@/features/accommodation/detail/components/AccommodationDetailContent'
import { accommodationDetailQueryOptions, unitAvailabilityQueryOptions } from '@/api/accommodation/hooks'
import { Button } from '@/components/ui/button'
import { HttpError } from '@/api/http'
import { buildAccommodationHead } from '@/lib/seo/accommodationHead'

const detailParamsSchema = z.object({
  slug: z.string().min(1),
  unitSlug: z.string().min(1),
})

function DetailErrorComponent({ error }: ErrorComponentProps) {
  let title = 'Chyba při načítání'
  let message = 'Nepodařilo se načíst detail ubytování. Zkuste to prosím znovu.'

  if (error instanceof HttpError && error.status === 404) {
    title = 'Ubytování nenalezeno'
    message = 'Toto ubytování neexistuje nebo bylo odstraněno.'
  }

  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">{title}</h1>
      <p className="mb-6 max-w-sm text-slate-500">{message}</p>
      <Button asChild>
        <Link to="/ubytovani/hledat">Zpět na vyhledávání</Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_layout/ubytovani/$slug/$unitSlug/')({
  component: AccommodationDetailPage,
  errorComponent: DetailErrorComponent,
  params: {
    parse: (params) => detailParamsSchema.parse(params),
    stringify: (params) => ({ slug: params.slug, unitSlug: params.unitSlug }),
  },
  loader: async ({ context, params }) => {
    let accommodation
    try {
      accommodation = await context.queryClient.query({
        ...accommodationDetailQueryOptions(params.slug, params.unitSlug),
        staleTime: 'static',
      })
    } catch (error) {
      // An unknown slug must surface as a real 404, not an SSR 500.
      if (error instanceof HttpError && error.status === 404) {
        throw notFound()
      }
      throw error
    }
    // Prices and the discount badge render server-side too (otherwise they pop
    // in during hydration); a failing calendar must not cost the page.
    await context.queryClient
      .query({
        ...unitAvailabilityQueryOptions(accommodation.unit.id),
        staleTime: 'static',
      })
      .catch(() => undefined)
    return accommodation
  },
  head: ({ loaderData }) => (loaderData ? buildAccommodationHead(loaderData) : {}),
})

function AccommodationDetailPage() {
  const { slug, unitSlug } = Route.useParams()

  // Keyed so switching to a sibling unit starts with a fresh date selection
  // and fires its own detail-view tracking.
  return <AccommodationDetailContent key={`${slug}/${unitSlug}`} slug={slug} unitSlug={unitSlug} />
}
